import React, {ChangeEvent, Component} from 'react';
import {connect} from 'react-redux';
import {AnyAction, bindActionCreators, Dispatch} from 'redux';

import _ from 'lodash';

import {TreeDragDropEvent, TreeExpandedEvent} from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import {DropdownChangeEvent, Dropdown} from 'primereact/dropdown';
import {Checkbox} from 'primereact/checkbox';
import {Button} from 'primereact/button';
import {Splitter, SplitterPanel} from 'primereact/splitter';

import Item from '~/shared/src/ci/Item';


import * as DataActions from '~/frontend/src/actions/data';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Joi from '~/shared/src/utilities/joiExtended';
import {StoreState} from '~/frontend/src/store';
import ItemTree, {AllProps as ItemTreeProps} from '~/frontend/src/components/base/shared/tree/ItemTree';
import {keys} from 'ts-transformer-keys';
import loadData from '~/frontend/src/actions/data/loadData';
import unsetParam from '~/frontend/src/actions/panel/common/unsetParam';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import {clearElusiveData} from '~/frontend/src/actions/elusiveData';
import {DATA_ADD_REFERENCE, DATA_DELETE_ITEM, DATA_DELETE_REFERENCE, DATA_UPDATE_ITEM} from '~/frontend/src/constants';


const defaultProps = {
    // the ciId of the root item
    rootItemId: 0 as number,

    // The ci-data context of this component
    dataContext: 'cypher' as string,
    schema: 'DATA' as Schema,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status
};

type DefaultProps = typeof defaultProps;

type Props = {
    uid : string;

    // CI-Data as stored in redux store
    items : { [ key : string ] : Item };

    // Version of CI-Data as stored in redux store
    itemsVersion : string | number;
    cypherConfig : string[] | number;

    // action functions
    deleteReference : Function;
    addReference : Function;
    clearCiData : Function;
    loadData : typeof loadData;
    unsetParam : typeof unsetParam;
    clearElusiveData : typeof clearElusiveData;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;
};

type AllProps = Props & DefaultProps;
export type ComponentProps = Partial<DefaultProps> & Props;

type State = Partial<{
    // DB schema
    schema : YnSchema;
    // the ciId of the currently selected tree node
    ciId : number;
    selectedNodeKey : string | null;
    treeData : TreeNode.TreeNode[];
    clearData : boolean;
    cypherQuery : string;
    ownCypherQueries : string[];
}>;


const Actions = Object.assign( { clearElusiveData, loadData, unsetParam }, DataActions );

/**
 * Playground to test Tree implementation
 */
class TreeTest extends Component<AllProps, State> {


    // panel manager config
    static panelType = 'TreeTest';
    static panelTabTitle = 'TreeTest';
    static panelTabIcon = 'fa fa-stream';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.treeTest';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( Joi.object( {
            panelType    : Joi.string().valid( TreeTest.panelType ),
            schema       : Joi.string().valid( 'DATA' ),
            panelTabTitle: Joi.string().default( TreeTest.panelTabTitle ),
            panelTabIcon : Joi.string().default( TreeTest.panelTabIcon )
        } ) );

    static defaultProps = defaultProps;

    static cypherQueries = [
        {
            cypher: 'MATCH (:GUID {itemKey: "10003214"})<-[:belongsTo]-(:Stage {all: true})',
            schema: 'DATA'
        },
        {
            cypher: 'MATCH (:GUID {itemKey: "10003214"})<-[:belongsTo]-(stage:Stage {all: true}) OPTIONAL MATCH (stage)<-[:belongsTo]-(:LogicalServer {all: true})',
            schema: 'DATA'
        },
        {
            cypher: 'MATCH (app:Application {itemKey: "ADE"})<-[:isMainMenuFor]-(menu:Menu) OPTIONAL MATCH (menu)<-[:isChildOf *1..5]-(menuItem:MenuItem {all:true}) OPTIONAL MATCH (menuItem)<-[:isIconFor]-(i:Image) OPTIONAL MATCH (menuItem)<-[:isLabelFor]-(:Message {all:true}) OPTIONAL MATCH (menuItem)<-[:isCalledBy]-(:PanelConfig {all:true})',
            schema: 'CFG'
        }
    ];

    static initialState = {
        // DB schema
        schema: 'DATA',
        // the ciId of the currently selected tree node
        ciId            : false,
        treeData        : [],
        clearData       : false,
        ownCypherQueries: []
    };

    state = Object.assign(
        {},
        TreeTest.initialState,
        {
            cypherQuery: TreeTest.cypherQueries[ 0 ].cypher,
            schema     : TreeTest.cypherQueries[ 0 ].schema ?? 'DATA'
        },
        JSON.parse( localStorage.getItem( 'TreeTest' ) ?? 'null' )
    );


    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state.schema = this.props.schema;
        this.onSelectQuery = this.onSelectQuery.bind( this );
        this.onTreeLoad = this.onTreeLoad.bind( this );
        this.onSelectTreeNode = this.onSelectTreeNode.bind( this );
        this.onButtonClick = this.onButtonClick.bind( this );
    }

    /**
     * Set state and save it to localStorage
     * @param {Object} nextState
     */
    setAndPersistState( nextState : State ) {
        this.setState( nextState, () => {
            localStorage.setItem( 'TreeTest', JSON.stringify( this.state ) );
        } );
    }

    /**
     * Helper functions to consolidate state setting from dom events
     * @param stateKey
     * @param eventKey
     * @param e
     */
    stateSetter( stateKey : keyof State, eventKey : keyof ChangeEvent | TreeExpandedEvent, e : ChangeEvent | TreeExpandedEvent ) {
        this.setAndPersistState( { [ stateKey ]: e[ eventKey as keyof typeof e ] } as unknown as State );
    }

    /**
     * Adds relevant actions to list store
     *
     * @param action
     */
    getActionText( action : AnyAction ) {
        let refSetId,
            retVal = 'n/a';

        if ( action.paramKey ) {
            retVal = `Set ${action.paramKey} to ${JSON.stringify( action.paramValue )}`;
        }
        else if ( action.data?.data?.items ) {
            retVal = `${Object.keys( action.data?.data?.items ).length} items loaded`;
        }

        switch ( action.type ) {
            case DATA_UPDATE_ITEM:
                retVal = `Changed attributes of item ${( action as any ).itemId}`;
                break;
            case DATA_DELETE_ITEM:
                retVal = `Deleted item ${( action as any ).itemId} (e.g. changed attribute 'statusId' to 21)`;
                break;
            /*case 'DATA_UNDELETE_ITEM':
                retVal = `Deleted item ${( action as UndeleteItemAction ).itemId} (e.g. changed attribute 'statusId' to 7)`;
                break;
            case 'DATA_UPDATE_PROPERTY':
                retVal = `Changed property '${( action as UpdatePropertyAction ).propertyId}' to ${action.value}`;
                break;*/
            case DATA_ADD_REFERENCE:
                refSetId = ( action as any ).reference.referenceSetId;
                retVal = `Reference '${( action as any ).reference.srcItemId}-${refSetId}-${( action as any ).reference.destItemId}' added`;
                break;
            case DATA_DELETE_REFERENCE:
                retVal = `Reference '${( action as any ).referenceId}' deleted`;
                break;
        }
        return retVal;
    }


    /**
     * Event called when moving a drag node over a possible drop target.
     *
     * @param {TreeNode} dragNode the dragged tree node
     * @param {TreeNode} dropNode the drop (target) node
     * @return {boolean} return false if dropping is not allowed on the currently hovered node
     */
    onValidateDrop( dragNode : TreeNode.TreeNode, dropNode : TreeNode.TreeNode ) : boolean {

        // basic checks
        /*let retVal: boolean = false;

        if ( dragNode && dropNode ) {
            const targetItem = dropNode.data.ciItem,
                  dragItem = dragNode.data.ciItem;
            // check if reference set of drag node is allowed on target node
            // FIXME: only ask metaModel
            // retVal = targetItem.canRegisterReferenceOfSet( dragItem.references[ 0 ].referenceSetId, dragItem.itemTypeId );
        }

        return retVal;*/
        return false;
    }

    /**
     * Drop handler. Moved tree node, removes old and sets new reference
     */
    onDrop( e : TreeDragDropEvent ) {
        const { dragNode, dropNode } = e;
        const dragItem = dragNode.data.ciItem;
        const dropItem = dropNode.data.ciItem;

        // delete old reference and add new one
        this.props.deleteReference( this.props.dataContext, dragNode.data.referenceId );
        this.props.addReference( this.props.dataContext, {
            srcId         : dragItem.id,
            destId        : dropItem.id,
            referenceSetId: dragNode.data.referenceSetId
        } );

    }

    /**
     * Handles "get results" button click
     * @param {MouseEventHandler<HTMLButtonElement>} e
     */
    onButtonClick( e : React.MouseEvent<HTMLButtonElement> ) {
        const { dataContext, uid } = this.props;
        const { cypherQuery, schema, clearData } = this.state;
        if ( clearData ) {
            this.props.clearElusiveData( this.props.dataContext! );
            this.props.unsetParam?.( this.props.uid, 'selectedKey' );
            this.props.unsetParam?.( this.props.uid, 'expandedKeys' );

        }

        this.props.loadData?.( 'tree', dataContext!, uid, undefined, schema,
            Object.assign( {
                extraDataKeys: [ 'rootItemId', 'cypherConfig', 'treeData' ]
            }, {
                params: {
                    schema,
                    treeCypher: cypherQuery
                }
            } )
        );

    }

    /**
     * Handles selection of tree node. Basically sets ciId in components state to display the appropriate form beside the tree.
     *
     * @param {Event} e the DOM event
     */
    onSelectQuery( e : DropdownChangeEvent ) {
        this.setAndPersistState( _.isObject( e.value )
            ? {
                // conversion to any is needed as isObject check implies type Object!
                cypherQuery: ( e.value as any ).cypher as string,
                schema     : ( e.value as any ).schema || 'DATA' as Schema
            }
            : {
                cypherQuery: e.value
            } );
    }

    /**
     * TreeLoad handler, used to fill most recently used list of CypherQueries
     */
    onTreeLoad() {
        const { ownCypherQueries, cypherQuery, schema } = this.state;
        // append current cypherQuery to list of queries
        if ( !TreeTest.cypherQueries.concat( ownCypherQueries ).find( ( cq => cq.cypher === cypherQuery && cq.schema === schema ) ) ) {
            this.setAndPersistState( { ownCypherQueries: ownCypherQueries.concat( { cypher: cypherQuery, schema } ).slice( -20 ) } );
        }

    }

    /**
     * Handles selection of tree node. Basically sets ciId in components state to display the appropriate form beside the tree.
     *
     * @param selectedKey
     * @param selectedPayloadId
     */
    onSelectTreeNode( selectedKey : string, selectedPayloadId : number ) {
        this.setAndPersistState( {
            ciId: selectedPayloadId
        } );
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const { cypherQuery, schema, clearData, ciId, ownCypherQueries } = this.state;
        const { rootItemId, dataContext, uid, status, subPanelProps } = this.props;
        const selectedKey = subPanelProps?.[ 'tree' ]?.selectedKey;
        const selectedNodeId = subPanelProps?.[ 'tree' ]?.selectedNodeId;

        // TODO: for now, the selectedKey is the ciId, this must be changed as items could be appear multiple times
        const ciItem = global.data[ dataContext ]?.items?.[ selectedNodeId || rootItemId ];

        return (
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <div className="fluid" style={{ padding: 20, flex: '0 0 auto' }}>
                    <div className="field grid">
                        <label htmlFor="cypher" className="col-12 md:col-1">Cypher</label>
                        <div className="col-12 md:col-11">
                            <Dropdown
                                id="cypher"
                                style={{width: '100%'}}
                                value={cypherQuery}
                                onChange={this.onSelectQuery}
                                options={TreeTest.cypherQueries.concat( ownCypherQueries )}
                                optionLabel="cypher"
                                editable
                            />
                        </div>
                    </div>
                    <div className="fluid formgrid grid">
                        <div className="field p-formgroup-inline col-12 md:col-2">
                            <label htmlFor="schema" className="col-12 md:col-2">Schema</label>
                            <Dropdown
                                id="schema"
                                value={schema}
                                // TODO: check how to use bind correctly in this case
                                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                                // @ts-ignore
                                onChange={this.stateSetter.bind( this, 'schema', 'value' )}
                                options={[ { schema: 'DATA' }, { schema: 'CFG' } ]}
                                optionValue="schema"
                                optionLabel="schema"/>
                        </div>
                        <div className="field col-12 md:col-1">
                            <label htmlFor="clear">Clear</label>
                            <Checkbox
                                id="clear"
                                checked={clearData}
                                // TODO: check how to use bind correctly in this case
                                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                                // @ts-ignore
                                onChange={this.stateSetter.bind( this, 'clearData', 'checked' )}
                            />
                        </div>
                        <div className="field col-12 md:col-1">
                            <Button type="button" label="Get Result" onClick={this.onButtonClick}/>
                        </div>

                    </div>
                </div>
                <Splitter className="mb-5 yn-flex-layout-child yn-content">
                    <SplitterPanel size={20}>
                        <ItemTree
                            {..._.pick( subPanelProps?.[ 'tree' ], keys<ItemTreeProps>() ) as ItemTreeProps}
                            //className="yn-sidebar-content"
                            dataContext={dataContext}
                            uid={uid + '_tree'}
                            parentUid={uid}
                            schema={schema}
                            apiCommand="tree"
                            apiParams={{
                                schema,
                                treeCypher: cypherQuery
                            }}
                            onSelectItem={this.onSelectTreeNode}
                            onLoad={this.onTreeLoad}
                            disableLoader
                        />
                    </SplitterPanel>
                    <SplitterPanel>
                        {ciItem ?
                            <AttributesForm
                                uid={uid + '_attributes'}
                                dataContext={uid}

                                //{...panelProps}

                                schema={schema}
                                status={status}

                                item={ciItem}

                                showEmptyProperties={false}
                                showPropertyDefaults={false}
                                rawMode={false}
                                showAdditionalRawNames={false}

                                editable={false}
                            /> : <></>}
                    </SplitterPanel>
                </Splitter>
            </LoadingMaskPanel>
        );
    }

}


// redux stuff

/**
 * Map relevant data from redux store state to component props
 * @param state
 * @param ownProps
 * @return {{lastAction: (*|boolean), cypherConfig: (*|boolean), itemsVersion, rootItemId: (*|boolean), items: {}}}
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const dataContext = ownProps.dataContext || TreeTest.defaultProps.dataContext;
    return {};
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( TreeTest );
